import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, BellOff, Radio, X } from "lucide-react";
import { toast } from "sonner";
import { api, browserRegion, errMsg, timeAgo } from "@/lib/api";
import { FactActions } from "@/components/FactActions";

const KEY = "middle:breaking-dismissed";

export const useBreaking = (tick = 0) => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    const gone = JSON.parse(sessionStorage.getItem(KEY) || "[]");
    api.get("/feed/breaking", { params: { cc: browserRegion() } }).then((r) => setItems(r.data.filter((b) => !gone.includes(b.id)))).catch(() => {});
  }, [tick]);
  const dismiss = (id) => { sessionStorage.setItem(KEY, JSON.stringify([...JSON.parse(sessionStorage.getItem(KEY) || "[]"), id])); setItems((xs) => xs.filter((x) => x.id !== id)); };
  const unfollow = async (b) => {
    try {
      const { data } = await api.post(`/feed/breaking/${b.kind}/${b.id}/unfollow`);
      setItems((xs) => xs.filter((x) => x.id !== b.id));
      toast.success("You won't see this story in your Feed", {
        action: { label: "Undo", onClick: () => api.delete(`/feed/breaking/${b.kind}/${b.id}/unfollow`, { params: { refollow: data.was_following } }).then(() => setItems((xs) => [b, ...xs])) },
      });
    } catch (e) { toast.error(errMsg(e)); }
  };
  return { items, dismiss, unfollow };
};

export const BreakingCard = ({ b, onDismiss, onUnfollow }) => (
  <div className="relative overflow-hidden rounded-2xl bg-[#13352C] p-5 text-white shadow-sm fade-up" data-testid={`breaking-card-${b.id}`}>
    <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em]">
      <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-500 px-2 py-0.5 text-white"><Radio size={11} className="animate-pulse" />Breaking</span>
      <span className="truncate text-emerald-100/80">Facts · {b.source_name || "approved source"} · {b.area_label}</span>
      <button onClick={() => onDismiss(b.id)} className="ml-auto rounded-full p-1 text-emerald-100/70 hover:bg-white/10 hover:text-white" aria-label="Hide for now" data-testid={`breaking-dismiss-${b.id}`}><X size={15} /></button>
    </div>
    <p className="mt-3 font-serif text-2xl leading-snug">{b.title}</p>
    {b.summary && <p className="mt-1.5 line-clamp-2 text-sm text-emerald-50/90">{b.summary} <span className="text-emerald-100/60">· {timeAgo(b.updated_at)}</span></p>}
    <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-emerald-100/70">
      <Link to={b.link || `/live/${b.id}`} className="inline-flex items-center gap-1 rounded-full bg-white px-4 py-1.5 text-sm font-medium text-[#13352C] hover:bg-emerald-50" data-testid={`breaking-open-${b.id}`}>Read the fact check<ArrowUpRight size={14} /></Link>
      {(b.live || []).map((ev) => <Link key={ev.id} to={`/live/${ev.id}`} className="inline-flex items-center gap-1 rounded-full border border-white/25 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-white/10" data-testid={`breaking-live-${b.id}-${ev.id}`}><Radio size={14} />Middle Live: {ev.title}</Link>)}
      <button onClick={() => onUnfollow(b)} className="inline-flex items-center gap-1 rounded-full border border-white/25 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-white/10" data-testid={`breaking-unfollow-${b.id}`}><BellOff size={14} />Unfollow</button>
      <Link to="/preferences" className="ml-1 underline-offset-2 hover:underline" data-testid={`breaking-prefs-${b.id}`}>Turn off all breaking</Link>
    </div>
    <FactActions f={b} kind="fact" inline className="mt-4 rounded-xl bg-white px-2 py-1 text-slate-700" />
  </div>
);

// Extra cards (breaking first, then Live updates) sit after the 2nd post, then after every 3 posts; any left over go at the end.
// Newest first across everything in the feed: member posts, breaking stories, activity, Fact and Live updates.
export const byTime = (posts, breaking, extras) => [...breaking, ...[...posts.map((p) => ({ type: "post", p, at: p.at || p.created_at })), ...extras]
  .sort((a, b) => (b.at || "").localeCompare(a.at || ""))];
